import React, { useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import {
  Button,
  Checkbox,
  Divider,
  LinearProgress,
  Theme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useParams } from "react-router";
import { useHistory } from "../../../../../shared/hooks/useHistory";
import { useDispatch, useSelector } from "react-redux";

import useStyles from "./view-task.styles";
import { Header } from "../../../../../shared/components/header";
import { buildBreadcrumbNavigationConfig } from "../../../../../shared/helpers/buildBreadcrumbNavigationConfig";
import {
  asyncUpdateParticipantTodo,
  asyncGetParticipantTodo,
  asyncUpdateStatusParticipantTodo,
} from "../../../../../todos/actions/todoActions";
import { setError } from "../../../../../shared/request/actions/requestActions";
import { getTodoContent } from "../../../../../todos/service";
import { getErrorMessageFromRequestError } from "../../../../../shared/helpers/messages/request";
import { ContentCard } from "../../../../../shared/components/contentCard/contentCard";
import EditableDate from "./editable-date.component";
import { EditPageAndPencilIcon } from "../../../../../shared/icons/EditPageAndPencilIcon";
import { Task } from "../../../../../models/redux/task.model";
import { RootState } from "../../../../../store";

interface Request {
  isFetching: boolean;
  type: string;
  statusCode: number;
  message: string;
  isOpen: boolean;
}

interface Selector {
  request: Request;
  currentTodo: Task;
  isSmsDisabled: boolean;
}

interface Params {
  taskId: string;
}

export const ViewTask = () => {
  const { taskId } = useParams<Params>();
  const classes = useStyles();
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const history = useHistory();
  const [contents, setContents] = useState([]);
  const [localIsFetching, setLocalIsFetching] = useState(false);
  const [smsCheckState, setSmsCheckState] = useState(false);
  const [emailCheckState, setEmailCheckState] = useState(false);

  const { request, currentTodo, isSmsDisabled } = useSelector<
    RootState,
    Selector
  >((state) => {
    return {
      request: state.request.request,
      currentTodo: state.todosReducer.currentTodo,
      isSmsDisabled:
        state.profile.mobile === null || state.profile.mobile === "",
    };
  });

  useEffect(() => {
    if (currentTodo?.reminderTypeId === 3) {
      setSmsCheckState(true);
      setEmailCheckState(true);
    } else if (currentTodo?.reminderTypeId === 2) {
      setSmsCheckState(true);
      setEmailCheckState(false);
    } else if (currentTodo?.reminderTypeId === 1) {
      setSmsCheckState(false);
      setEmailCheckState(true);
    }
  }, [currentTodo.reminderTypeId]);

  useEffect(() => {
    dispatch(asyncGetParticipantTodo(taskId));
  }, [taskId, dispatch]);

  useEffect(() => {
    const fetchFullContent = async () => {
      if (dispatch && currentTodo.content.length) {
        setLocalIsFetching(true);
        try {
          const contentIds = currentTodo.content.map((c) => c.contentId);
          const response = await getTodoContent(contentIds);
          setContents(response.data.data);
        } catch (error) {
          const message = getErrorMessageFromRequestError(error);
          dispatch(setError({ isOpen: true, message, type: "error" }));
        } finally {
          setLocalIsFetching(false);
        }
      }
    };
    fetchFullContent();
  }, [currentTodo.content.length, dispatch, currentTodo.content]);

  const handleCheckClick = async () => {
    dispatch(asyncUpdateStatusParticipantTodo(taskId, !currentTodo.completed));
  };

  const getReminderTypeId = (reminderTextMessage, reminderEmail) => {
    let type = 0;
    if (reminderTextMessage && reminderEmail) {
      type = 3;
    } else if (reminderTextMessage) {
      type = 2;
    } else if (reminderEmail) {
      type = 1;
    }
    return type;
  };

  const handleOnSaveDate = (date, smsCheck, emailCheck) => {
    const data = {
      title: currentTodo.title,
      description: currentTodo.description,
      categoryId: currentTodo.categoryId,
      content: currentTodo.content,
      dueDate: date,
      completed: currentTodo.completed,
      todoId: currentTodo.todoId,
      originId: currentTodo.originId,
      focusAreaId: currentTodo.focusAreaId,
      score: currentTodo.score,
      reminderTypeId: date ? getReminderTypeId(smsCheck, emailCheck) : 0,
    };
    dispatch(asyncUpdateParticipantTodo(data));
  };

  const handleEditClick = () => {
    history.push(`${history.location.pathname}/edit`);
  };

  const listPath = "/tasks";
  const listNav = buildBreadcrumbNavigationConfig("Tasks", listPath);
  const viewItemNav = buildBreadcrumbNavigationConfig(currentTodo.title);
  const navigation = [listNav, viewItemNav];

  const participantTaskOrigin = 1;

  return (
    <Header navigation={navigation}>
      <Grid container>
        <Grid item className={classes.titleContainer}>
          <Grid container>
            <Grid item xs={10}>
              <Typography
                className={classes.bottom}
                variant="h1"
                color="textPrimary"
              >
                Task: {currentTodo.title}
              </Typography>
            </Grid>
            <Grid item xs={2} className={classes.actions}>
              {currentTodo.originId === participantTaskOrigin && (
                <Button
                  className={classes.editTaskButton}
                  startIcon={<EditPageAndPencilIcon />}
                  onClick={handleEditClick}
                >
                  {!sm && "Edit Task"}
                </Button>
              )}
            </Grid>
          </Grid>
          <Typography
            className={classes.bottom}
            variant="h3"
            color="textPrimary"
          >
            Description
          </Typography>
          <Typography className={classes.bottom} color="textPrimary">
            {currentTodo.description}
          </Typography>
          <EditableDate
            date={currentTodo.dueDate}
            isSmsDisabled={isSmsDisabled}
            smsCheck={smsCheckState}
            emailCheck={emailCheckState}
            onSave={handleOnSaveDate}
          />
        </Grid>
      </Grid>
      <Divider className={classes.bottom} />

      {currentTodo.content.length > 0 && (
        <>
          <Typography
            className={classes.bottom}
            variant="h3"
            color="textPrimary"
          >
            Content Information
          </Typography>
          <Grid container className={classes.cards} spacing={2}>
            {contents.map((content) => (
              <Grid item key={`content-${content.contentId}`}>
                <ContentCard
                  imageUrl={content.imagePath}
                  title={content.title}
                  contentId={content.contentId}
                  resourceType={content.resourceType}
                  minutes={content.duration}
                />
              </Grid>
            ))}
          </Grid>
          <Divider className={classes.bottom} />
        </>
      )}
      {(request.isFetching || localIsFetching) && (
        <div className={classes.root}>
          <LinearProgress color="primary" />
        </div>
      )}
      <Typography className={classes.doneCheck} color="textPrimary">
        <Checkbox
          className={classes.checkbox}
          checked={currentTodo.completed}
          onChange={handleCheckClick}
          color="primary"
        />
        {currentTodo.completed ? "Done" : "Mark as Done"}
      </Typography>
    </Header>
  );
};
